<script setup lang="ts">
import { Check, Copy, Download, Printer } from 'lucide';
import { type CloseEvent } from '@/components/base/dialog/Dialog.vue';
import NoticeBox from '@legacy/components/common/NoticeBox.vue';
import {SphButton} from '@/components/base/button';
import SphCheckbox from '@/components/base/checkbox';
import SphDialog from '@/components/base/dialog/Dialog.vue';
import SphField from '@/components/base/field';
import { ref } from 'vue';

defineOptions({
    compatConfig: { MODE: 3 },
})

const props = defineProps({
    codes: {
        type: Array as () => string[],
        required: true,
    }
});

const emit = defineEmits(['close']);

const isOpen = ref(true);
const copied = ref(false);
const userReceivedCodes = ref(false);
const userReceivedCodesChecked = ref(false);

const close = ({ prevent }: CloseEvent) => {
    if (!userReceivedCodes.value) {
        userReceivedCodesChecked.value = true;
        prevent();
        return;
    }
    emit('close');
};

const print = () => {
    window.print();
};
const copyToClipboard = async () => {
    const text = props.codes.join('\n');
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        try {
            await navigator.clipboard.writeText(text);
            copied.value = true;
            setTimeout(() => {
                copied.value = false;
            }, 2000);
        } catch (err) {
            console.error('Clipboard write failed', err);
        }
    }
};

const downloadCodes = () => {
    const text = props.codes.join('\n');
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'backup-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};
</script>

<template>
    <SphDialog
        v-model:state="isOpen"
        variant="info"
        :title="$t('Backup-Codes')"
        :persistent="true"
        @before-close="close"
    >
        <p class="kgr-row kgr-hidden-print">
            {{ $t('Diese Backup-Codes bitte an einem sicheren Ort aufbewahren. Jeder Code kann einmalig verwendet werden, falls kein Zugriff auf die Authenticator-App besteht.') }}
        </p>
        <NoticeBox
            variant="warning"
            class="kgr-hidden-print"
        >
            <template #body>
                <span class="kgr-text kgr-text--extraSmall">{{ $t('Die Backup-Codes werden nicht mehr angezeigt, sobald dieses Fenster geschlossen wurde!') }}</span>
            </template>
        </NoticeBox>

        <div class="codes-grid">
            <code
                v-for="(code, index) in codes"
                :key="index"
                class="backup-code"
            >
                {{ code }}
            </code>
        </div>

        <div class="kgr-hidden-print flex flex-wrap justify-center gap-2 mt-4">
            <SphButton
                variant="ghost"
                :icon="Printer"
                :label="$t('Drucken')"
                @click="print"
            />
            <SphButton
                variant="ghost"
                :icon="copied ? Check : Copy"
                :label="copied ? $t('Kopiert!') : $t('Kopieren')"
                @click="copyToClipboard"
            />
            <SphButton
                variant="ghost"
                :icon="Download"
                :label="$t('Herunterladen')"
                @click="downloadCodes"
            />
        </div>

        <div class="kgr-hidden-print flex gap-2 mt-6 mx-auto">
            <SphField
                v-slot="field"
                inline
                compact
                required
                class="[&>[data-slot=field-content]]:flex-none justify-center mx-auto"
                :errors="userReceivedCodesChecked && !userReceivedCodes ? [$t('Bestätige, dass du die Backup-Codes erhalten hast.')] : null"
                :label="$t('Ich habe meine Backup Codes gespeichert.')"
            >
                <SphCheckbox
                    v-bind="field"
                    v-model="userReceivedCodes"
                />
            </SphField>
        </div>
    </SphDialog>
</template>

<style scoped lang="scss">
.codes-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.5rem;
}

.backup-code {
    font-family: monospace;
    font-size: 0.9rem;
    background: #f5f5f5;
    padding: 0.5rem 0.75rem;
    border-radius: 4px;
    text-align: center;
    letter-spacing: 0.05em;
}

@media print {
    :deep(.dialog-footer),
    :deep(.dialog-header) {
        display: none !important;
    }
    :deep(.sph-dialog) {
        box-shadow: none;
    }
}
</style>
